import { feedbackListSearch } from '../feedback/api'
import { sessionListSearch } from '../sessions/api'
import { contentHistoryDetailReturn } from '../activity/content-history-model'
import { catalogDetailHref, catalogDetailReturn, type CatalogDetail, type DetailPage } from '../content/catalog-detail-page'
import { catalogListParams, type CatalogResult } from '../content/catalog-types.js'
import { CATALOG_PATHS, catalogHref, type CatalogPage } from '../content/catalog-navigation.js'
import { personLabel, type PersonIdentity } from '../common/person-label.js'
import { useQueryClient } from '@tanstack/react-query'
import { Icon } from '@speaking-coach/ui'
import { useCallback, useSyncExternalStore } from 'react'
import { Link, matchPath, useLocation } from 'react-router-dom'

import { queryKeys } from '../api/query-keys'
import type { CohortDetail } from '../cohorts/types'
import {
  allTopics,
  contentTopicParentDestination,
} from '../content/content-model'
import { catalogBreadcrumbs, contentPageBreadcrumbs } from '../content/catalog-navigation.js'
import type { ContentDashboard, ContentTopicSource, DiagnosticDetail } from '../content/types.js'
import type {
  RevisionComparison,
  RevisionDetail,
  RevisionHistory,
  RevisionIdentity,
  RevisionKind,
  RevisionList,
} from '../content/revision-types.js'
import type { HrUser } from '../hr_users/types'
import { ADMIN_NAVIGATION_GROUPS } from '../navigation/admin-navigation'

interface BreadcrumbItem {
  label: string
  to: string
}

/**
 * On a History route (PROV-AUDIT, `…/history`) the entity itself joins
 * the trail as a LINKED parent — the crumb is a second way back to the
 * details view, beside the header's "Back to details" — and "History"
 * becomes the page heading. On the details route the entity stays the
 * heading, never a duplicate final crumb.
 */
function withHistoryCrumb(
  items: BreadcrumbItem[],
  historyMode: boolean,
  entity: BreadcrumbItem,
): BreadcrumbItem[] {
  return historyMode ? [...items, entity] : items
}

function clientFilteredListDestination(
  section: 'cohorts' | 'hr-users',
  clientId: string,
): string {
  return `/admin/${section}?clientId=${encodeURIComponent(clientId)}`
}

/** The immediate parent shared by the breadcrumb and top-bar close action. */
export function adminParentDestination(
  pathname: string,
  search = '',
  contentFallback?: { level: string; theme: string },
): BreadcrumbItem | null {
  // A History route's immediate parent IS the entity's details view —
  // the final crumb and the close action must keep sharing one
  // destination, and both lead back out of the mode.
  if (matchPath('/admin/feedback/:feedbackId', pathname)) return { label: 'Feedback', to: `/admin/feedback${feedbackListSearch(new URLSearchParams(search))}` }
  if (matchPath('/admin/sessions/:sessionId', pathname)) return { label: 'Call history', to: `/admin/sessions${sessionListSearch(new URLSearchParams(search))}` }
  if (pathname === '/admin/content/history') return null
  if (matchPath('/admin/content/diagnostics/:programId', pathname)) return { label: 'Curriculum', to: '/admin/content' }
  if (pathname.endsWith('/history')) {
    // The student details route carries an /edit suffix its siblings
    // do not.
    const studentHistory = matchPath('/admin/students/:userId/history', pathname)
    return {
      label: 'Details',
      to:
        studentHistory !== null
          ? `/admin/students/${studentHistory.params.userId ?? ''}/edit`
          : pathname.slice(0, -'/history'.length),
    }
  }

  const cohort =
    matchPath('/admin/clients/:clientId/cohorts/new', pathname) ??
    matchPath('/admin/clients/:clientId/cohorts/:cohortId', pathname)
  if (cohort !== null) {
    return {
      label: 'Cohorts',
      to: clientFilteredListDestination(
        'cohorts',
        cohort.params.clientId ?? '',
      ),
    }
  }

  const hrUser =
    matchPath('/admin/clients/:clientId/hr-users/new', pathname) ??
    matchPath('/admin/clients/:clientId/hr-users/:userId', pathname)
  if (hrUser !== null) {
    return {
      label: 'HR users',
      to: clientFilteredListDestination(
        'hr-users',
        hrUser.params.clientId ?? '',
      ),
    }
  }

  if (
    matchPath('/admin/students/new', pathname) !== null ||
    matchPath('/admin/students/batch', pathname) !== null ||
    matchPath('/admin/students/:userId/edit', pathname) !== null
  ) {
    return { label: 'Students', to: '/admin/students' }
  }

  if (
    matchPath('/admin/admins/new', pathname) !== null ||
    matchPath('/admin/admins/:userId', pathname) !== null
  ) {
    return { label: 'Cooori admins', to: '/admin/admins' }
  }

  if (matchPath('/admin/email-suppressions/:emailHash', pathname) !== null) {
    return {
      label: 'Undeliverable addresses',
      to: '/admin/email-suppressions',
    }
  }

  if (
    matchPath('/admin/clients/new', pathname) !== null ||
    matchPath('/admin/clients/:clientId', pathname) !== null
  ) {
    return { label: 'Clients', to: '/admin/clients' }
  }

  if (matchPath('/admin/content/scenarios/:topicId', pathname) !== null || matchPath('/admin/content/diagnostics/:programId', pathname) !== null) {
    const { label, to } = contentTopicParentDestination(
      new URLSearchParams(search),
      contentFallback,
    )
    return { label, to }
  }

  return null
}

function revisionIdentityBreadcrumbs(
  identity: RevisionIdentity,
  returnTo: string | null,
): BreadcrumbItem[] {
  if (identity.kind === 'tutor') {
    return [
      { label: 'Tutors', to: catalogHref('tutors') },
      {
        label: identity.label,
        to: returnTo?.startsWith('/admin/content/tutors/')
          ? returnTo
          : `/admin/content/tutors/${encodeURIComponent(identity.id)}`,
      },
    ]
  }

  if (identity.scenarioType === 'onboarding') {
    const diagnosticMatch = returnTo
      ? matchPath(
          '/admin/content/diagnostics/:programId',
          returnTo.split('?').at(0) ?? '',
        )
      : null
    const program = identity.programs.find(
      (item) => item.id === diagnosticMatch?.params.programId,
    )
    const items: BreadcrumbItem[] = [{ label: 'Programs', to: catalogHref('programs') }]
    if (program) {
      items.push(
        {
          label: program.label,
          to: `/admin/content/programs/${encodeURIComponent(program.id)}`,
        },
        {
          label: 'Scenarios',
          to: `/admin/content/scenarios?${new URLSearchParams({ program: program.id, kind: 'diagnostic' })}`,
        },
      )
      items.push({
        label: identity.label,
        to: returnTo ?? `/admin/content/diagnostics/${encodeURIComponent(program.id)}`,
      })
    } else {
      items.push({ label: 'Scenarios', to: '/admin/content/scenarios?kind=diagnostic' })
    }
    return items
  }

  const items: BreadcrumbItem[] = [{ label: 'Programs', to: catalogHref('programs') }]
  const programId = identity.program?.id
  const levelId = identity.level?.id
  const themeId = identity.theme?.id
  if (identity.program) {
    items.push({
      label: identity.program.label,
      to: `/admin/content/programs/${encodeURIComponent(identity.program.id)}`,
    })
  }
  if (identity.level && programId) {
    items.push({
      label: identity.level.label,
      to: catalogHref('levels', { program: programId, level: levelId }),
    })
  }
  if (identity.theme && programId && levelId) {
    items.push({
      label: identity.theme.label,
      to: catalogHref('themes', {
        program: programId,
        level: levelId,
        theme: themeId,
      }),
    })
  }
  items.push({
    label: 'Scenarios',
    to: catalogHref('scenarios', {
      program: programId,
      level: levelId,
      theme: themeId,
    }),
  })
  items.push({
    label: identity.label,
    to: returnTo?.startsWith('/admin/content/scenarios/')
      ? returnTo
      : `/admin/content/scenarios/${encodeURIComponent(identity.id)}`,
  })
  return items
}

function useContentBreadcrumbItems(pathname: string, search: string): BreadcrumbItem[] {
  const revisionCompareMatch = matchPath('/admin/content/revisions/:kind/:identityId/compare', pathname)
  const rawRevisionDetailMatch = matchPath('/admin/content/revisions/:kind/:identityId/:revisionId', pathname)
  const revisionDetailMatch = revisionCompareMatch ? null : rawRevisionDetailMatch
  const revisionListMatch = matchPath('/admin/content/revisions/:kind/:identityId', pathname)
  const revisionMatch = revisionCompareMatch ?? revisionDetailMatch ?? revisionListMatch
  const revisionKind: RevisionKind = revisionMatch?.params.kind === 'tutor' ? 'tutor' : 'lesson'
  const revisionIdentityId = revisionMatch?.params.identityId ?? ''
  const revisionParams = new URLSearchParams(search)
  const revisionPage = Math.max(1, Number(revisionParams.get('page')) || 1)
  const revisionList = useCachedQueryData<RevisionList>(
    queryKeys.contentRevisions(revisionKind, revisionIdentityId, revisionPage, 20),
  )
  const revisionHistory = useCachedQueryData<RevisionHistory>(
    queryKeys.contentRevisionHistory(
      revisionKind,
      revisionIdentityId,
      Math.max(1, Number(revisionParams.get('historyPage')) || 1),
      20,
    ),
  )
  const revisionDetail = useCachedQueryData<RevisionDetail>(
    queryKeys.contentRevision(
      revisionKind,
      revisionIdentityId,
      revisionDetailMatch?.params.revisionId ?? '',
    ),
  )
  const revisionComparison = useCachedQueryData<RevisionComparison>(
    queryKeys.contentRevisionComparison(
      revisionKind,
      revisionIdentityId,
      revisionParams.get('from') ?? '',
      revisionParams.get('to') ?? '',
    ),
  )
  const detailMatch = matchPath('/admin/content/:directory/:contentId', pathname)
  const detailPage = detailMatch?.params.directory
  const detail = useCachedQueryData<CatalogDetail>(queryKeys.catalogDetail(detailPage ?? '', detailMatch?.params.contentId ?? ''))
  const isDetail = detailPage === 'programs' || detailPage === 'levels' || detailPage === 'themes' || detailPage === 'tutors'
  const dashboard = useCachedQueryData<ContentDashboard>(queryKeys.content)
  const topicMatch = matchPath('/admin/content/scenarios/:topicId', pathname)
  const topicSource = useCachedQueryData<ContentTopicSource>(
    queryKeys.contentTopicSource(topicMatch?.params.topicId ?? ''),
  )
  const diagnostic = useCachedQueryData<DiagnosticDetail>(queryKeys.contentDiagnostic(pathname.split('/').at(-1) ?? ''))
  const catalogPage = (Object.entries(CATALOG_PATHS) as [CatalogPage, string][]).find(([, path]) => path === pathname)?.[0]
  const catalog = useCachedQueryData<CatalogResult>(queryKeys.catalogList(catalogPage ?? 'programs', catalogListParams(catalogPage ?? 'programs', new URLSearchParams(search))))
  const historyDetail = pathname === '/admin/content/history' ? contentHistoryDetailReturn(new URLSearchParams(search)) : undefined
  if (historyDetail) return [{ label: 'Content details', to: historyDetail }]
  if (revisionMatch) {
    const identity =
      revisionComparison?.identity ??
      revisionDetail?.identity ??
      revisionList?.identity ??
      revisionHistory?.identity
    if (!identity) return []
    const returnTo = revisionParams.get('returnTo')
    const items = revisionIdentityBreadcrumbs(identity, returnTo)
    if (revisionCompareMatch || revisionDetailMatch) {
      const revisionSearch = new URLSearchParams()
      if (returnTo) revisionSearch.set('returnTo', returnTo)
      items.push({
        label: 'Revisions',
        to: `/admin/content/revisions/${identity.kind}/${encodeURIComponent(identity.id)}${revisionSearch.size ? `?${revisionSearch}` : ''}`,
      })
    }
    return items
  }
  if (isDetail) {
    const ancestors: BreadcrumbItem[] = []
    if (detail?.program) {
      ancestors.push(
        { label: 'Programs', to: catalogHref('programs') },
        { label: detail.program.label, to: catalogDetailHref('programs', detail.program.id) },
      )
    }
    if (detail?.level) ancestors.push({ label: detail.level.label, to: catalogDetailHref('levels', detail.level.id) })
    return [...ancestors, { label: detailPage.charAt(0).toUpperCase() + detailPage.slice(1), to: catalogDetailReturn(detailPage as DetailPage, new URLSearchParams(search)) }]
  }
  if (catalogPage && catalog) {
    const { scope } = catalog
    if (!scope.valid || !scope.program || catalogPage === 'programs') return []
    const items = [
      { label: 'Programs', to: catalogHref('programs') },
      { label: scope.program.label, to: catalogDetailHref('programs', scope.program.id) },
    ]
    if (scope.level && catalogPage !== 'levels') items.push({ label: scope.level.label, to: catalogHref('levels', { program: scope.program.id, level: scope.level.id }) })
    if (scope.theme && catalogPage === 'scenarios') items.push({ label: scope.theme.label, to: catalogHref('themes', { program: scope.program.id, level: scope.level?.id, theme: scope.theme.id }) })
    return items
  }
  const match = topicMatch
  if (match) {
    const topicDashboard = topicSource?.level
      ? {
          levels: [topicSource.level],
          programs: [{
            id: topicSource.level.programId,
            name: topicSource.level.programName,
            diagnostic: null,
          }],
        }
      : dashboard
    const location = topicDashboard && allTopics(topicDashboard).find(row => row.topic.id === match.params.topicId)
    const params = new URLSearchParams(search)
    const parent = contentTopicParentDestination(params, location ? { level: location.level.programLevelId, theme: location.theme.id } : undefined)
    const ancestry = location
      ? new URLSearchParams({
          program: location.level.programId,
          level: location.level.programLevelId,
          theme: location.theme.id,
        })
      : new URLSearchParams(parent.to.split('?')[1] ?? '')
    const ancestors = topicDashboard ? catalogBreadcrumbs('scenarios', topicDashboard, ancestry) : []
    return [...ancestors, { label: parent.label, to: parent.to }]
  }
  return contentPageBreadcrumbs(pathname, search, dashboard, diagnostic)
}

export function useAdminParentDestination(pathname: string, search = ''): BreadcrumbItem | null {
  const items = useContentBreadcrumbItems(pathname, search)
  if (pathname.startsWith('/admin/content')) {
    if ((Object.values(CATALOG_PATHS) as string[]).includes(pathname)) return null
    return items.at(-1) ?? null
  }
  return adminParentDestination(pathname, search)
}

/**
 * Observe data a route already owns without starting a second request merely
 * to decorate the shell. Detail pages put their row in TanStack Query; this
 * turns a generic parent crumb into the client name when that row arrives.
 */
function useCachedQueryData<T>(queryKey: readonly unknown[]): T | undefined {
  const queryClient = useQueryClient()
  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      queryClient.getQueryCache().subscribe(onStoreChange),
    [queryClient],
  )

  return useSyncExternalStore(
    subscribe,
    () => queryClient.getQueryData<T>(queryKey),
    () => undefined,
  )
}

function breadcrumbLabel(
  value: string | null | undefined,
  fallback: string,
): string {
  const trimmed = value?.trim()
  return trimmed ? trimmed : fallback
}

function clientTrail(
  clientId: string,
  clientName: string | null | undefined,
): BreadcrumbItem[] {
  return [
    { label: 'Clients', to: '/admin/clients' },
    {
      label: breadcrumbLabel(clientName, 'Client'),
      to: `/admin/clients/${clientId}`,
    },
  ]
}

function ClientNestedBreadcrumbs({
  clientId,
  section,
}: {
  clientId: string
  section: BreadcrumbItem
}): JSX.Element {
  const client = useCachedQueryData<{ name: string }>(queryKeys.client(clientId))
  return (
    <BreadcrumbTrail
      items={[...clientTrail(clientId, client?.name), section]}
    />
  )
}

function CohortBreadcrumbs({
  clientId,
  cohortId,
  historyMode = false,
}: {
  clientId: string
  cohortId: string
  historyMode?: boolean
}): JSX.Element {
  const cohort = useCachedQueryData<CohortDetail>(
    queryKeys.cohort(clientId, cohortId),
  )
  // The cohort page also owns the client-detail query for classification.
  // Prefer that authoritative name: older/detail payloads can carry an empty
  // clientName, which must never become an invisible breadcrumb link.
  const client = useCachedQueryData<{ name: string }>(queryKeys.client(clientId))
  const clientName = breadcrumbLabel(
    client?.name,
    breadcrumbLabel(cohort?.clientName, 'Client'),
  )
  return (
    <BreadcrumbTrail
      items={withHistoryCrumb(
        [
          ...clientTrail(clientId, clientName),
          {
            label: 'Cohorts',
            to: clientFilteredListDestination('cohorts', clientId),
          },
        ],
        historyMode,
        {
          label: breadcrumbLabel(cohort?.name, 'Cohort'),
          to: `/admin/clients/${clientId}/cohorts/${cohortId}`,
        },
      )}
    />
  )
}

function HrUserBreadcrumbs({
  clientId,
  userId,
  historyMode = false,
}: {
  clientId: string
  userId: string
  historyMode?: boolean
}): JSX.Element {
  const hrUser = useCachedQueryData<HrUser>(queryKeys.hrUser(clientId, userId))
  const client = useCachedQueryData<{ name: string }>(queryKeys.client(clientId))
  const clientName = breadcrumbLabel(
    client?.name,
    breadcrumbLabel(hrUser?.clientName, 'Client'),
  )
  return (
    <BreadcrumbTrail
      items={withHistoryCrumb(
        [
          ...clientTrail(clientId, clientName),
          {
            label: 'HR users',
            to: clientFilteredListDestination('hr-users', clientId),
          },
        ],
        historyMode,
        {
          label: breadcrumbLabel(personLabel(hrUser), 'HR user'),
          to: `/admin/clients/${clientId}/hr-users/${userId}`,
        },
      )}
    />
  )
}

function ClientBreadcrumbs({
  clientId,
  historyMode = false,
}: {
  clientId: string
  historyMode?: boolean
}): JSX.Element {
  const client = useCachedQueryData<{ name: string }>(queryKeys.client(clientId))
  return (
    <BreadcrumbTrail
      items={withHistoryCrumb(
        [{ label: 'Clients', to: '/admin/clients' }],
        historyMode,
        {
          label: breadcrumbLabel(client?.name, 'Client'),
          to: `/admin/clients/${clientId}`,
        },
      )}
    />
  )
}

function StudentBreadcrumbs({
  userId,
  historyMode = false,
}: {
  userId: string
  historyMode?: boolean
}): JSX.Element {
  const student = useCachedQueryData<PersonIdentity>(queryKeys.student(userId))
  return (
    <BreadcrumbTrail
      items={withHistoryCrumb(
        [{ label: 'Students', to: '/admin/students' }],
        historyMode,
        {
          label: breadcrumbLabel(personLabel(student), 'Student'),
          to: `/admin/students/${userId}/edit`,
        },
      )}
    />
  )
}

function CoooriAdminBreadcrumbs({
  userId,
  historyMode = false,
}: {
  userId: string
  historyMode?: boolean
}): JSX.Element {
  const admin = useCachedQueryData<PersonIdentity>(queryKeys.coooriAdmin(userId))
  return (
    <BreadcrumbTrail
      items={withHistoryCrumb(
        [{ label: 'Cooori admins', to: '/admin/admins' }],
        historyMode,
        {
          label: breadcrumbLabel(personLabel(admin), 'Admin'),
          to: `/admin/admins/${userId}`,
        },
      )}
    />
  )
}

function BreadcrumbTrail({ items }: { items: BreadcrumbItem[] }): JSX.Element {
  const navigationItems = ADMIN_NAVIGATION_GROUPS.flatMap(
    (group) => group.items,
  )

  return (
    <nav
      aria-label="Breadcrumb"
      className="min-w-0 max-w-[40vw] shrink font-display text-body"
    >
      <ol className="flex min-w-0 items-center">
        {items.map((item, index) => {
          const destinationPath = item.to.split('?')[0]
          const sectionIcon = navigationItems.find(
            (section) => section.to === destinationPath && section.label === item.label,
          )?.icon
          return (
            <li
              key={`${item.label}-${index}`}
              className={`min-w-0 items-center ${index === items.length - 1 ? 'flex' : 'hidden sm:flex'}`}
            >
              {index > 0 && (
                <Icon
                  name="breadcrumb"
                  className="mx-1.5 shrink-0 text-text-faint"
                />
              )}
              <Link
                to={item.to}
                className="flex min-w-0 items-center gap-1.5 text-text-secondary hover:text-text-primary hover:underline"
              >
                {sectionIcon !== undefined && (
                  <Icon
                    name={sectionIcon}
                    size={14}
                    data-slot="breadcrumb-section-icon"
                    className="shrink-0 text-text-faint"
                  />
                )}
                <span className="truncate">{item.label}</span>
              </Link>
            </li>
          )
        })}
        <li aria-hidden="true" className="flex shrink-0 items-center">
          <Icon name="breadcrumb" className="mx-1 text-text-faint" />
        </li>
      </ol>
    </nav>
  )
}

export function AdminBreadcrumbs(): JSX.Element | null {
  const { pathname, search } = useLocation()
  const contentItems = useContentBreadcrumbItems(pathname, search)

  if (matchPath('/admin/feedback/:feedbackId', pathname)) return <BreadcrumbTrail items={[adminParentDestination(pathname, search)!]} />
  if (matchPath('/admin/sessions/:sessionId', pathname)) return <BreadcrumbTrail items={[adminParentDestination(pathname, search)!]} />
  if (pathname.startsWith('/admin/content')) return contentItems.length ? <BreadcrumbTrail items={contentItems} /> : null

  // History routes first: they extend their entity's trail with the
  // entity itself as a linked parent.
  const cohortHistory = matchPath(
    '/admin/clients/:clientId/cohorts/:cohortId/history',
    pathname,
  )
  if (cohortHistory !== null) {
    return (
      <CohortBreadcrumbs
        clientId={cohortHistory.params.clientId ?? ''}
        cohortId={cohortHistory.params.cohortId ?? ''}
        historyMode
      />
    )
  }
  const hrUserHistory = matchPath(
    '/admin/clients/:clientId/hr-users/:userId/history',
    pathname,
  )
  if (hrUserHistory !== null) {
    return (
      <HrUserBreadcrumbs
        clientId={hrUserHistory.params.clientId ?? ''}
        userId={hrUserHistory.params.userId ?? ''}
        historyMode
      />
    )
  }
  const studentHistory = matchPath('/admin/students/:userId/history', pathname)
  if (studentHistory !== null) {
    return (
      <StudentBreadcrumbs
        userId={studentHistory.params.userId ?? ''}
        historyMode
      />
    )
  }
  const adminHistory = matchPath('/admin/admins/:userId/history', pathname)
  if (adminHistory !== null) {
    return (
      <CoooriAdminBreadcrumbs
        userId={adminHistory.params.userId ?? ''}
        historyMode
      />
    )
  }
  const clientHistory = matchPath('/admin/clients/:clientId/history', pathname)
  if (clientHistory !== null) {
    return (
      <ClientBreadcrumbs
        clientId={clientHistory.params.clientId ?? ''}
        historyMode
      />
    )
  }

  const newCohort = matchPath('/admin/clients/:clientId/cohorts/new', pathname)
  if (newCohort !== null) {
    return (
      <ClientNestedBreadcrumbs
        clientId={newCohort.params.clientId ?? ''}
        section={{
          label: 'Cohorts',
          to: clientFilteredListDestination(
            'cohorts',
            newCohort.params.clientId ?? '',
          ),
        }}
      />
    )
  }

  const cohort = matchPath(
    '/admin/clients/:clientId/cohorts/:cohortId',
    pathname,
  )
  if (cohort !== null) {
    return (
      <CohortBreadcrumbs
        clientId={cohort.params.clientId ?? ''}
        cohortId={cohort.params.cohortId ?? ''}
      />
    )
  }

  const newHrUser = matchPath('/admin/clients/:clientId/hr-users/new', pathname)
  if (newHrUser !== null) {
    return (
      <ClientNestedBreadcrumbs
        clientId={newHrUser.params.clientId ?? ''}
        section={{
          label: 'HR users',
          to: clientFilteredListDestination(
            'hr-users',
            newHrUser.params.clientId ?? '',
          ),
        }}
      />
    )
  }

  const hrUser = matchPath(
    '/admin/clients/:clientId/hr-users/:userId',
    pathname,
  )
  if (hrUser !== null) {
    return (
      <HrUserBreadcrumbs
        clientId={hrUser.params.clientId ?? ''}
        userId={hrUser.params.userId ?? ''}
      />
    )
  }

  const student = matchPath('/admin/students/:userId/edit', pathname)
  if (student !== null) {
    return <StudentBreadcrumbs userId={student.params.userId ?? ''} />
  }

  const suppression = matchPath(
    '/admin/email-suppressions/:emailHash',
    pathname,
  )
  if (suppression !== null) {
    return (
      <BreadcrumbTrail
        items={[
          {
            label: 'Undeliverable addresses',
            to: '/admin/email-suppressions',
          },
        ]}
      />
    )
  }

  const coooriAdmin = matchPath('/admin/admins/:userId', pathname)
  if (coooriAdmin !== null) {
    return <CoooriAdminBreadcrumbs userId={coooriAdmin.params.userId ?? ''} />
  }

  if (matchPath('/admin/clients/new', pathname) !== null) {
    return (
      <BreadcrumbTrail
        items={[{ label: 'Clients', to: '/admin/clients' }]}
      />
    )
  }

  const client = matchPath('/admin/clients/:clientId', pathname)
  if (client !== null) {
    return <ClientBreadcrumbs clientId={client.params.clientId ?? ''} />
  }

  const nestedStatic: Array<{
    pattern: string
    parent: BreadcrumbItem
  }> = [
    {
      pattern: '/admin/students/new',
      parent: { label: 'Students', to: '/admin/students' },
    },
    {
      pattern: '/admin/students/batch',
      parent: { label: 'Students', to: '/admin/students' },
    },
    {
      pattern: '/admin/admins/new',
      parent: { label: 'Cooori admins', to: '/admin/admins' },
    },
  ]
  const nested = nestedStatic.find(({ pattern }) =>
    Boolean(matchPath(pattern, pathname)),
  )
  if (nested !== undefined) {
    return <BreadcrumbTrail items={[nested.parent]} />
  }

  return null
}
